/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Heart, Star, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const APP_STORE_URL = "https://apps.apple.com/app/cauldron/id000000000"; // placeholder until App Store ID assigned
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.cauldron";

/**
 * RatePromptDialog — web fallback modal for the in-app rate prompt.
 *
 * Shown by `useRatePrompt().showWebModal` when the user is eligible AND not
 * on a native build. Two-stage flow: first asks how the user feels; if
 * positive, deep-links to the app store (or web review). If neutral/negative,
 * routes them to support so they vent privately instead of one-starring us.
 */
export default function RatePromptDialog({ open, onClose }) {
  const [feeling, setFeeling] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const record = async (response) => {
    try {
      await api.post("/me/rate-prompt-response", { response, platform: "web" });
    } catch {
      /* noop */
    }
  };

  const onLove = async () => {
    setFeeling("love");
    await record("rated");
    // Open store on a new tab; if the user is on a phone web browser, the
    // store deep-link will route to the matching mobile store.
    const ua = navigator.userAgent.toLowerCase();
    const url = ua.includes("android") ? PLAY_STORE_URL : APP_STORE_URL;
    window.open(url, "_blank", "noopener");
    setSubmitted(true);
  };

  const onMeh = async () => {
    setFeeling("meh");
    await record("later");
    setSubmitted(true);
    toast.success("Thank you — we'll keep improving.");
  };

  const onNeverAgain = async () => {
    await record("opted_out");
    onClose();
    toast.success("Got it — we won't ask again.");
  };

  const close = async () => {
    if (!feeling) {
      await record("dismissed");
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
      data-testid="rate-prompt-overlay"
    >
      <div
        className="parchment p-8 max-w-md w-full relative space-y-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="rate-prompt-dialog"
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"
          aria-label="Close"
          data-testid="rate-prompt-close-btn"
        >
          <X size={18} strokeWidth={1.75} />
        </button>

        {!submitted ? (
          <>
            <div className="text-center space-y-3">
              <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-secondary/15 border border-secondary/40">
                <Heart size={28} strokeWidth={1.5} className="text-secondary" />
              </div>
              <h2 className="font-serif text-2xl">Enjoying Cauldron?</h2>
              <p className="text-sm text-foreground/65">
                You've been around a while — we'd love to know how it's been.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onMeh}
                className="flex-1 border border-secondary/30 px-4 py-3 rounded-sm font-serif text-sm hover:border-secondary/60 transition-colors"
                data-testid="rate-prompt-meh-btn"
              >
                Could be better
              </button>
              <button
                onClick={onLove}
                className="flex-1 bg-primary text-primary-foreground px-4 py-3 rounded-sm font-serif text-sm uppercase tracking-wider font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5"
                data-testid="rate-prompt-love-btn"
              >
                <Star size={14} strokeWidth={2} /> Love it
              </button>
            </div>
            <button
              onClick={onNeverAgain}
              className="block w-full text-[10px] uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground/80 text-center pt-1"
              data-testid="rate-prompt-never-btn"
            >
              Don't ask me again
            </button>
          </>
        ) : (
          <div className="text-center space-y-4">
            {feeling === "love" ? (
              <>
                <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-secondary/15 border border-secondary text-secondary">
                  <Star size={28} strokeWidth={1.5} />
                </div>
                <h2 className="font-serif text-2xl">Thank you</h2>
                <p className="text-sm text-foreground/65">
                  The store should have opened in a new tab. Your honest review helps other seekers find their way home.
                </p>
              </>
            ) : (
              <>
                <h2 className="font-serif text-2xl">Tell us more</h2>
                <p className="text-sm text-foreground/65">
                  What would make Cauldron better for you?
                </p>
                <a
                  href="mailto:hello@thecauldron.app?subject=Feedback%20on%20Cauldron"
                  className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-xs font-semibold hover:bg-primary/90 transition-colors"
                  data-testid="rate-prompt-feedback-btn"
                >
                  Email us
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="block w-full text-xs uppercase tracking-wider text-foreground/55 hover:text-foreground pt-2"
              data-testid="rate-prompt-done-btn"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
